import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import type { RootState } from '../store';

import { PtypStyle, Style } from '@remodeling/budget-expect';
import { IdValue, PyOption, SelectedStyle, StyleCategory } from '@/types';
import { STYLE_SPACE_TYPE_COMMON } from '@/constants';

// Define a type for the slice state

export interface BudgetExpectState {
  pyOptions: PyOption[];
  currentPyOption: PyOption | null;
  currentRoomOption: IdValue | null;
  currentBathOption: IdValue | null;
  isDetermined: boolean;
  totalPrice: number;
  commonList: Style[];
  categoryList: StyleCategory[];
  styleList: PtypStyle[];
  currentStyleList: SelectedStyle[];
  selectedBathStyleCnt: number;
  selectedRoomStyleCnt: number;
}

// Define the initial state using that type
const initialState: BudgetExpectState = {
  pyOptions: [],
  currentPyOption: null,
  currentRoomOption: null,
  currentBathOption: null,
  isDetermined: false,
  totalPrice: 0,
  commonList: [],
  categoryList: [],
  styleList: [],
  currentStyleList: [],
  selectedBathStyleCnt: 0,
  selectedRoomStyleCnt: 0,
};

export const budgetExpectSlice = createSlice({
  name: 'budgetExpect',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setPyOptions: (state, action: PayloadAction<PyOption[]>) => {
      state.pyOptions = action.payload;
    },
    selectPyOption: (state, action: PayloadAction<PyOption>) => {
      state.currentPyOption = action.payload;
      state.currentRoomOption = null;
      state.currentBathOption = null;
      state.isDetermined = false;
      state.totalPrice = 0;
      state.commonList = [];
      state.currentStyleList = [];
      state.selectedBathStyleCnt = 0;
      state.selectedRoomStyleCnt = 0;
    },
    selectRoom: (state, action: PayloadAction<IdValue | null>) => {
      state.currentRoomOption = action.payload;
    },
    selectBath: (state, action: PayloadAction<IdValue | null>) => {
      state.currentBathOption = action.payload;
    },
    reset: (state) => {
      state.currentPyOption = null;
      state.currentRoomOption = null;
      state.currentBathOption = null;
      state.isDetermined = false;
      state.totalPrice = 0;
      state.commonList = [];
      state.currentStyleList = [];
      state.selectedBathStyleCnt = 0;
      state.selectedRoomStyleCnt = 0;
    },
    determine: (state) => {
      state.isDetermined = !state.isDetermined;
    },
    choiceBack: (state) => {
      state.isDetermined = false;
    },
    setCommonList: (state, action: PayloadAction<Style[]>) => {
      state.totalPrice = action.payload.reduce(
        (value, style) => value + style.totalAmount,
        0,
      );
      state.commonList = action.payload;
      state.currentStyleList = action.payload.map((style) => ({
        ...style,
        spaceType: STYLE_SPACE_TYPE_COMMON,
      }));
    },
    setStyleList: (state, action: PayloadAction<PtypStyle[]>) => {
      state.styleList = action.payload;
      state.categoryList = action.payload.reduce((list, ptypStyle) => {
        list.push({
          name: ptypStyle.spaceName,
          type: ptypStyle.spaceType,
          children: ptypStyle.style.reduce((stack, style) => {
            const type = style.spaceDetailType;
            if (
              type &&
              type !== ptypStyle.spaceType &&
              _.findIndex(stack, { type }) < 0
            ) {
              stack.push({
                name: style.spaceDetailName,
                type: style.spaceDetailType,
              });
            }
            return stack;
          }, [] as Pick<StyleCategory, 'name' | 'type'>[]),
        });
        return list;
      }, [] as StyleCategory[]);
    },
    addStyle: (state, action: PayloadAction<SelectedStyle>) => {
      state.currentStyleList.push(action.payload);
      state.totalPrice += action.payload.totalAmount;
    },
    selectedBathStyleCnt: (state, action: PayloadAction<number>) => {
      state.selectedBathStyleCnt = action.payload;
    },
    selectedRoomStyleCnt: (state, action: PayloadAction<number>) => {
      state.selectedRoomStyleCnt = action.payload;
    },
    removeStyle: (
      state,
      action: PayloadAction<Pick<SelectedStyle, 'styleMId'>>,
    ) => {
      const index = _.findIndex(state.currentStyleList, action.payload);
      if (index >= 0) {
        const values = state.currentStyleList.splice(index, 1);
        state.totalPrice -= values[0].totalAmount;
      }
    },
  },
});

export const {
  setPyOptions,
  selectPyOption,
  selectRoom,
  selectBath,
  reset,
  determine,
  setCommonList,
  setStyleList,
  addStyle,
  removeStyle,
  selectedBathStyleCnt,
  selectedRoomStyleCnt,
} = budgetExpectSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectPyOptions = (state: RootState) =>
  state.budgetExpect.pyOptions;
export const selectCurrentPyOption = (state: RootState) =>
  state.budgetExpect.currentPyOption;
export const selectCurrentRoomOption = (state: RootState) =>
  state.budgetExpect.currentRoomOption;
export const selectCurrentBathOption = (state: RootState) =>
  state.budgetExpect.currentBathOption;
export const selectIsDetermined = (state: RootState) =>
  state.budgetExpect.isDetermined;
export const selectIsChoiceBack = (state: RootState) =>
  state.budgetExpect.isDetermined;
export const selectTotalPrice = (state: RootState) =>
  state.budgetExpect.totalPrice;
export const selectCategoryList = (state: RootState) =>
  state.budgetExpect.categoryList;
export const selectStyleList = (state: RootState) =>
  state.budgetExpect.styleList;
export const selectCurrentStyleList = (state: RootState) =>
  state.budgetExpect.currentStyleList;
export const selectBathStyleCnt = (state: RootState) =>
  state.budgetExpect.selectedBathStyleCnt;
export const selectRoomStyleCnt = (state: RootState) =>
  state.budgetExpect.selectedRoomStyleCnt;

export default budgetExpectSlice;
